import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { customerApi, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { tWork, tCompletion, tVisit, tPeriod } from "@/i18n/translations";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ArrowLeft, Loader2, Upload, X, AlertTriangle, Clock, Image as ImageIcon, Video } from "lucide-react";
import { toast } from "sonner";

const COMPLETION_TIMES = [
  "As soon as possible", "Within 24 hours", "Within a few days",
  "Within a week", "Within 2 weeks", "This month", "Flexible / no rush",
];

export default function QuoteForm() {
  const nav = useNavigate();
  const loc = useLocation();
  const { customer } = useAuth();
  const { t, te, lang } = useLanguage();
  const urgency = loc.state?.urgency;

  const [workTypes, setWorkTypes] = useState([]);
  const [workType, setWorkType] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [budget, setBudget] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState(customer?.address || "");
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [visitPrefs, setVisitPrefs] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [visitPref, setVisitPref] = useState("");
  const [preferredTimes, setPreferredTimes] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProp, setSelectedProp] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [fuelTypes, setFuelTypes] = useState([]);
  const [plate, setPlate] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [odometer, setOdometer] = useState("");
  const [mmy, setMmy] = useState("");
  const [sparkQuote, setSparkQuote] = useState(false);
  const [parkingNotes, setParkingNotes] = useState("");
  const [safeConfirmed, setSafeConfirmed] = useState(false);
  const isVehicle = !!vehicleType && workType === vehicleType;

  useEffect(() => {
    customerApi.get("/properties").then(({ data }) => {
      const active = data.filter((p) => !p.archived);
      setProperties(active);
      const def = active.find((p) => p.is_default) || active[0];
      if (def) { setSelectedProp(def.id); setAddress(def.address); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!urgency) nav("/urgency");
  }, [urgency, nav]);

  useEffect(() => {
    customerApi.get("/meta/options").then(({ data }) => {
      setWorkTypes(data.work_types);
      setVisitPrefs(data.visit_preferences || []);
      setPeriods(data.time_periods || []);
      setVehicleType(data.vehicle_work_type || "");
      setFuelTypes(data.vehicle_fuel_types || []);
    }).catch(() => {});
  }, []);

  const handleFiles = async (files) => {
    setUploading(true);
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await customerApi.post("/upload", form);
        setMedia((m) => [...m, data]);
      } catch (e) {
        toast.error(te(apiError(e)));
      }
    }
    setUploading(false);
  };

  const removeMedia = (id) => setMedia((m) => m.filter((x) => x.id !== id));

  const submit = async () => {
    if (!workType) return toast.error(t("err_select_work"));
    if (!preferredTime) return toast.error(t("err_select_time"));
    if (!address.trim()) return toast.error(t("err_confirm_address"));
    let vehicle = null;
    if (isVehicle) {
      if (!plate.trim()) return toast.error(t("err_plate_required"));
      if (!fuelType) return toast.error(t("err_select_fuel"));
      if (!safeConfirmed) return toast.error(t("err_safe_location"));
      vehicle = {
        plate: plate.trim(), fuel_type: fuelType, odometer_km: odometer || null,
        make_model_year: mmy || null, spark_plug_quote: sparkQuote,
        parking_notes: parkingNotes || null, safe_location_confirmed: safeConfirmed,
      };
    }
    setBusy(true);
    try {
      const { data } = await customerApi.post("/quotes", {
        urgency, work_type: workType, preferred_time: preferredTime,
        budget: budget || null, description: description || null,
        address, media_ids: media.map((m) => m.id), language: lang,
        property_id: selectedProp || null,
        visit_preference: visitPref || null,
        preferred_times: preferredTimes.filter((pt) => pt.date || pt.period || pt.note),
        vehicle,
      });
      nav("/confirmation", { state: { ...data, media_count: media.length } });
    } catch (e) {
      toast.error(te(apiError(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-28">
      <LanguageSelector />
      <div className="max-w-md mx-auto px-6 py-8">
        <button data-testid="quote-back-btn" onClick={() => nav("/urgency")}
          className="flex items-center gap-2 text-primary font-medium mb-6">
          <ArrowLeft className="h-5 w-5" /> {t("back")}
        </button>

        <div className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold w-fit ${urgency === "Urgent" ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"}`}>
          {urgency === "Urgent" ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />} {urgency === "Urgent" ? t("urgent") : t("not_urgent")}
        </div>
        <h2 className="font-display text-2xl font-bold text-primary mt-4">{t("quote_request")}</h2>
        <p className="text-muted-foreground text-sm mt-1">{t("required_note")}</p>

        <div className="mt-6 space-y-6 animate-slide-up">
          <div>
            <label className="block text-sm font-semibold text-primary mb-3">{t("type_of_work")} *</label>
            <div className="grid grid-cols-1 gap-3">
              {workTypes.map((w, i) => (
                <button key={w} type="button" data-testid={`worktype-option-${i}`} onClick={() => setWorkType(w)}
                  className={`text-left rounded-2xl border p-4 text-sm font-medium transition-colors ${workType === w ? "border-primary bg-secondary text-primary ring-2 ring-primary" : "border-border bg-white text-foreground hover:border-primary/40"}`}>
                  <span className="flex items-center gap-2 flex-wrap">
                    {tWork(w, lang)}
                    {vehicleType && w === vehicleType && (
                      <span data-testid="mobile-service-badge" className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{t("mobile_service_badge")}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
            {isVehicle && (
              <div className="mt-3 rounded-2xl bg-secondary/60 p-4 text-xs text-secondary-foreground leading-relaxed space-y-2" data-testid="vehicle-service-desc">
                <p>{t("vehicle_service_desc")}</p>
                <p className="text-muted-foreground">{t("vehicle_scope_note")}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{t("preferred_time")} *</label>
            <div className="grid grid-cols-2 gap-3">
              {COMPLETION_TIMES.map((tm, i) => (
                <button key={tm} type="button" data-testid={`completion-option-${i}`} onClick={() => setPreferredTime(tm)}
                  className={`rounded-2xl border p-3 text-sm font-medium transition-colors ${preferredTime === tm ? "border-primary bg-secondary text-primary ring-2 ring-primary" : "border-border bg-white hover:border-primary/40"}`}>
                  {tCompletion(tm, lang)}
                </button>
              ))}
            </div>
          </div>

          {isVehicle && (
            <div data-testid="vehicle-details-section" className="rounded-2xl border-2 border-primary/30 bg-white p-4 space-y-4">
              <p className="text-sm font-bold text-primary">{t("vehicle_details")}</p>
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">{t("plate_label")} *</label>
                <input data-testid="vehicle-plate-input" value={plate}
                  onChange={(e) => setPlate(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                  placeholder="ABC123" className="input-lg uppercase" />
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed" data-testid="plate-privacy-notice">{t("plate_privacy_notice")}</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">{t("fuel_type")} *</label>
                <select data-testid="vehicle-fuel-select" value={fuelType} onChange={(e) => setFuelType(e.target.value)}
                  className="w-full h-12 rounded-2xl border border-input bg-white px-4 text-sm focus:ring-2 focus:ring-primary focus:outline-none">
                  <option value="">{t("fuel_placeholder")}</option>
                  {fuelTypes.map((f) => <option key={f} value={f}>{t(`fuel_${f}`)}</option>)}
                </select>
              </div>
              <label className="flex items-start gap-3 rounded-xl bg-secondary/60 p-3 cursor-pointer">
                <input data-testid="vehicle-safe-checkbox" type="checkbox" checked={safeConfirmed} onChange={(e) => setSafeConfirmed(e.target.checked)} className="mt-1 h-4 w-4" />
                <span className="text-xs text-foreground leading-relaxed">{t("safe_location_confirm")} *</span>
              </label>
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">{t("odometer_label")} <span className="text-muted-foreground font-normal">{t("optional")}</span></label>
                <input data-testid="vehicle-odometer-input" value={odometer} onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ""))} inputMode="numeric" className="input-lg" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">{t("make_model_year")} <span className="text-muted-foreground font-normal">{t("optional")}</span></label>
                <input data-testid="vehicle-mmy-input" value={mmy} onChange={(e) => setMmy(e.target.value)} className="input-lg" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="vehicle-spark-checkbox" type="checkbox" checked={sparkQuote} onChange={(e) => setSparkQuote(e.target.checked)} className="mt-1 h-4 w-4" />
                <span className="text-sm text-foreground">{t("spark_plug_opt")}</span>
              </label>
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">{t("parking_notes")} <span className="text-muted-foreground font-normal">{t("optional")}</span></label>
                <textarea data-testid="vehicle-parking-input" value={parkingNotes} onChange={(e) => setParkingNotes(e.target.value)} rows={2}
                  className="w-full rounded-2xl border border-input bg-white p-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{t("job_address")} *</label>
            {properties.length > 0 && (
              <select data-testid="quote-property-select" value={selectedProp}
                onChange={(e) => {
                  setSelectedProp(e.target.value);
                  const p = properties.find((x) => x.id === e.target.value);
                  if (p) setAddress(p.address);
                }}
                className="w-full h-12 mb-2 rounded-2xl border border-input bg-white px-4 text-sm focus:ring-2 focus:ring-primary focus:outline-none">
                <option value="">{t("add_new_property")}</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.nickname} — {p.address}</option>)}
              </select>
            )}
            <AddressAutocomplete value={address} onChange={(v) => { setAddress(v); setSelectedProp(""); }} testId="quote-address-input" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{t("budget_label")} <span className="text-muted-foreground font-normal">{t("optional")}</span></label>
            <input data-testid="quote-budget-input" value={budget} onChange={(e) => setBudget(e.target.value)}
              inputMode="numeric" placeholder={t("budget_ph")} className="input-lg" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{t("description_label")} <span className="text-muted-foreground font-normal">{t("optional")}</span></label>
            <textarea data-testid="quote-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
              rows={4} placeholder={t("description_ph")}
              className="w-full rounded-2xl border border-input bg-white p-4 text-base focus:ring-2 focus:ring-primary focus:outline-none" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{t("photos_videos")} <span className="text-muted-foreground font-normal">{t("optional")}</span></label>
            <label data-testid="quote-upload-label" className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-primary/30 bg-white py-8 cursor-pointer hover:bg-secondary/50 transition-colors">
              {uploading ? <Loader2 className="h-7 w-7 animate-spin text-primary" /> : <Upload className="h-7 w-7 text-primary" />}
              <span className="text-sm text-muted-foreground">{uploading ? t("uploading") : t("tap_add_media")}</span>
              <input data-testid="quote-file-input" type="file" accept="image/*,video/*" multiple className="hidden"
                onChange={(e) => e.target.files && handleFiles(e.target.files)} />
            </label>
            {media.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="quote-media-list">
                {media.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 rounded-full bg-secondary px-3 py-2 text-xs text-secondary-foreground">
                    {m.kind === "video" ? <Video className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
                    <span className="max-w-[120px] truncate">{m.filename}</span>
                    <button type="button" onClick={() => removeMedia(m.id)} data-testid={`remove-media-${m.id}`}>
                      <X className="h-4 w-4 hover:text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid="visit-section" className="rounded-2xl border border-border bg-white p-4">
            <label className="block text-sm font-semibold text-primary">{t("visit_section_title")} <span className="text-muted-foreground font-normal">{t("optional")}</span></label>
            <div className="mt-3 grid grid-cols-1 gap-2">
              {visitPrefs.map((v, i) => (
                <button key={v} type="button" data-testid={`visit-option-${i}`}
                  onClick={() => setVisitPref(visitPref === v ? "" : v)}
                  className={`text-left rounded-xl border p-3 text-sm font-medium transition-colors ${visitPref === v ? "border-primary bg-secondary text-primary ring-2 ring-primary" : "border-border hover:border-primary/40"}`}>
                  {tVisit(v, lang)}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{t("visit_optional_note")}</p>

            {visitPref && visitPref !== "Contact me first / No time preference" && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-primary">{t("preferred_visit_times")}</p>
                <div className="mt-2 space-y-3" data-testid="preferred-times">
                  {preferredTimes.map((pt, idx) => (
                    <div key={idx} className="rounded-xl border border-border p-3 space-y-2">
                      <input type="date" data-testid={`pref-date-${idx}`} value={pt.date || ""}
                        onChange={(e) => setPreferredTimes((a) => a.map((x, i) => i === idx ? { ...x, date: e.target.value } : x))}
                        className="w-full h-11 rounded-lg border border-input px-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
                      <select data-testid={`pref-period-${idx}`} value={pt.period || ""}
                        onChange={(e) => setPreferredTimes((a) => a.map((x, i) => i === idx ? { ...x, period: e.target.value } : x))}
                        className="w-full h-11 rounded-lg border border-input bg-white px-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none">
                        <option value="">{t("time_period")}</option>
                        {periods.map((p) => <option key={p} value={p}>{tPeriod(p, lang)}</option>)}
                      </select>
                      <input data-testid={`pref-note-${idx}`} value={pt.note || ""} placeholder={t("exact_note")}
                        onChange={(e) => setPreferredTimes((a) => a.map((x, i) => i === idx ? { ...x, note: e.target.value } : x))}
                        className="w-full h-11 rounded-lg border border-input px-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
                      <button type="button" data-testid={`pref-remove-${idx}`} onClick={() => setPreferredTimes((a) => a.filter((_, i) => i !== idx))}
                        className="text-xs text-destructive font-medium">{t("remove")}</button>
                    </div>
                  ))}
                </div>
                {preferredTimes.length < 3 && (
                  <button type="button" data-testid="add-preferred-time-btn" onClick={() => setPreferredTimes((a) => [...a, { date: "", period: "", note: "" }])}
                    className="mt-2 text-sm font-semibold text-primary">+ {t("add_time")}</button>
                )}
                <p className="mt-3 rounded-xl bg-accent p-3 text-xs text-accent-foreground" data-testid="request-only-note">{t("request_only_note")}</p>
              </div>
            )}
          </div>
        </div>
        <p data-testid="enquiry-notice" className="mt-6 rounded-2xl border border-border bg-secondary/40 p-4 text-xs text-muted-foreground leading-relaxed">{t("enquiry_notice")}</p>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur border-t border-border">
        <div className="max-w-md mx-auto px-6 py-4">
          <button data-testid="quote-submit-btn" onClick={submit} disabled={busy} className="btn-primary">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("submit_request")}
          </button>
        </div>
      </div>
    </div>
  );
}
